import { useCallback, useEffect, useState } from "react"
import { ReviewRecord } from "../../types/study/ReviewRecord"
import useWordlist from "../wordlist/useWordlist"
import { WordStudy } from "../../types/study/WordStudy"
import { advanceStudy, getNewReviewRecord, getUpdatedReviewRecord, initializeStudy } from "../../helpers/StudyHelper"
import { PrimitivesChallengeData, StandaloneChallengeData } from "../../types/study/ChallengeData"
import useError from "../useError"
import { supabase } from "../../api"
import useWarning from "../useWarning"
import { PostgrestError } from "@supabase/supabase-js"
import { getMinValue } from "../../utils/arrayUtils"
import useStudyInformation from "./useStudyInformation"

export default function useStudy(wordlistId: string | undefined, userId: string) {
    const { wordlist, isLoading: wordlistIsLoading, error: wordlistError } = useWordlist(wordlistId)
    const [currentWordlistLevelId, setCurrentWordlistLevelId] = useState<number>()
    const [reviewRecords, setReviewRecords] = useState<ReviewRecord[]>()
    const [wordStudy, setWordStudy] = useState<WordStudy>()
    const [challengePosition, setChallengePosition] = useState<number>(0)
    const [backdropOpen, setBackdropOpen] = useState<boolean>(false)
    const [mistakes, setMistakes] = useState<number>(0)
    const [loadNextReview, setLoadNextReview] = useState<boolean>(true)
    const resetStudyState = () => {
        setWordStudy(undefined)
        setMistakes(0)
        setChallengePosition(0)
    }
    const { handleError } = useError()
    const { handleWarning } = useWarning()
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<Error | PostgrestError>()

    const { studyInformation } =
        useStudyInformation(reviewRecords, wordStudy, wordlist?.wordlist_level.find(l => l.id === currentWordlistLevelId), mistakes)

    //#region Effects

    useEffect(() => {
        async function doAsync() {
            if (!wordlist)
                return

            const defaultWordlistLevel = getMinValue(wordlist.wordlist_level, 'index')

            if (defaultWordlistLevel) {
                setCurrentWordlistLevelId(defaultWordlistLevel.id)
                const { error } = await initializeStudy(userId, defaultWordlistLevel.wordlist_content, setReviewRecords)
                if (error)
                    setError(error)
            }
        }

        doAsync()
    }, [userId, wordlist])

    useEffect(() => {
        async function maybeAdvanceStudy() {
            const wordlistLevel = wordlist?.wordlist_level.find(l => l.id === currentWordlistLevelId)
            if (wordlistLevel && reviewRecords && loadNextReview) {
                setIsLoading(true)
                resetStudyState()
                const { error, warnings } = await advanceStudy(wordlistLevel.wordlist_content, reviewRecords, setWordStudy)
                if (error) {
                    setError(error)
                    return
                }
                if (warnings) {
                    warnings.forEach(w => handleWarning(w))
                }

                setLoadNextReview(false)
                setIsLoading(false)
            }
        }

        maybeAdvanceStudy()
    }, [reviewRecords, loadNextReview, handleWarning, wordlist?.wordlist_level, currentWordlistLevelId])

    useEffect(() => {
        async function recordReviewedRecord(reviewRecord: ReviewRecord) {
            const updatedRecord = getUpdatedReviewRecord(reviewRecord, mistakes)

            const { data: returnedRecord, error: postgresError } = await supabase.from("review_record").upsert(updatedRecord).select().single()
            if (postgresError) {
                setError(postgresError)
                return
            }

            setReviewRecords(prev => {
                const filteredPrevious = prev?.filter(r => r.id !== returnedRecord.id) ?? []
                return [...filteredPrevious, returnedRecord]
            })
        }

        async function doAsync() {
            if (!wordStudy?.challenges?.at(challengePosition)) {
                if (wordStudy?.reviewRecord)
                    await recordReviewedRecord(wordStudy?.reviewRecord)
            }
        }

        doAsync()
    }, [challengePosition, mistakes, wordStudy])

    useEffect(() => {
        if (error) {
            handleError(error)
            setIsLoading(false)
        }
    }, [error, handleError])

    //#endregion

    //#region Results

    const challenge = wordStudy?.challenges?.at(challengePosition)
    const charactersShown = challenge?.position ?? 0

    const onMistake = useCallback(() => {
        setMistakes(prev => prev + 1)
        setBackdropOpen(true)
    }, [])

    const recordNewRecord = useCallback(async (wordId: number | null, readingId: number, meaningId: number) => {
        const newRecord = getNewReviewRecord(userId, wordId, readingId, meaningId)

        const { data: returnedRecord, error: postgresError } = await supabase.from("review_record").upsert(newRecord).select().single()
        if (postgresError) {
            setError(postgresError)
            return
        }

        setReviewRecords(prev => {
            const filteredPrevious = prev?.filter(r => r.id !== returnedRecord.id) ?? []
            return [...filteredPrevious, returnedRecord]
        })
    }, [userId])

    const onContinue = useCallback(async () => {
        if (!wordStudy)
            return

        const standaloneChallenge = challenge as StandaloneChallengeData
        const primitivesChallenge = challenge as PrimitivesChallengeData
        if (standaloneChallenge?.falseChars || primitivesChallenge?.primitives) {
            setChallengePosition(prev => prev + 1)
            return
        }

        if (!wordStudy.reviewRecord) {
            const wordId = wordStudy.wordlistContent.dictionary_word?.id ?? null
            const readingId = wordStudy.wordlistContent.dictionary_reading.id
            const meaningId = wordStudy.wordlistContent.dictionary_meaning.id
            await recordNewRecord(wordId, readingId, meaningId)
        }

        setLoadNextReview(true)
    }, [challenge, recordNewRecord, wordStudy])

    const setBackdropClosed = useCallback(() => {
        setBackdropOpen(false)
    }, [])

    //#endregion

    return {
        wordlist,
        wordlistContent: wordStudy?.wordlistContent,
        challenge,
        charactersShown,
        backdropOpen,
        studyInformation,
        onContinue,
        onMistake,
        setBackdropClosed,
        isLoading: wordlistIsLoading || isLoading,
        error: wordlistError ?? error
    }
}
